import React, { useEffect, useRef } from 'react';
import propTypes from 'prop-types';
import axios from 'axios';
import AgencyHierarchyList, {
  AGENCY_TREE_NODE_ID_PREFIX
} from './AgencyHierarchyList';
import { cn } from 'lib/utils';
import AgencyTreeFindBar from 'tailwind-components/AgencyTreeFindBar/AgencyTreeFindBar';
import useAgencyTreeFind from 'tailwind-components/AgencyTreeFindBar/useAgencyTreeFind';
import { useSelector } from 'react-redux';
import { areOnboardingInvitesDisabled } from 'helpers/viewing-rules';
import useMobile from 'hooks/useMobile';

const EMPTY_ROOTS = [];

/**
 * O'Neill's Agency Hierarchy page: the agencies tree plus a Cmd+F-style find bar
 * @param agenciesTree - the agencies tree from redux
 * @param getAgenciesTree - action loading the tree
 * @param setLocalLoader - toggles the page loader
 */
export default function AgencyHierarchyView({
  agenciesTree,
  getAgenciesTree,
  setLocalLoader
}) {
  const userAgencyData = useSelector(state => state.user.zohoData.agencyData);
  const scrollContainerRef = useRef(null);
  const isMobile = useMobile();

  useEffect(() => {
    const cancelTokenSource = axios.CancelToken.source();
    if ((agenciesTree?.length ?? 0) > 0) {
      // A previously loaded tree renders right away; refresh it silently in
      // the background instead of blocking the page behind the loader.
      setLocalLoader(false);
      getAgenciesTree(() => {}, cancelTokenSource);
    } else {
      getAgenciesTree(setLocalLoader, cancelTokenSource);
    }
    return () => {
      cancelTokenSource.cancel('API Call Canceled');
    };
  }, []);

  const roots = agenciesTree ?? EMPTY_ROOTS;

  const treeIsEmpty = roots.length === 1 && roots[0].length === 0;

  const find = useAgencyTreeFind({
    roots: agenciesTree,
    nodeIdPrefix: AGENCY_TREE_NODE_ID_PREFIX,
    scrollContainerRef
  });

  return (
    <div
      className={cn(
        'flex min-h-0 flex-1 flex-col rounded-xl',
        'h-full overflow-hidden'
      )}
    >
      <div className='shrink-0 border-b border-grey-lighter bg-white p-4'>
        <header className='space-y-1'>
          <h2 className='text-2xl font-semibold! m-0 leading-none'>
            Agency Hierarchy
          </h2>
          <p className='m-0 text-sm leading-relaxed text-grey md:text-base'>
            View your agency&apos;s hierarchy, open an agency&apos;s dashboard,
            manage{' '}
            {areOnboardingInvitesDisabled(userAgencyData)
              ? 'settings'
              : 'settings and invites'}{' '}
            and see appointment details
          </p>
        </header>

        {!treeIsEmpty && (
          <div className='mt-4'>
            <AgencyTreeFindBar
              searchQuery={find.searchQuery}
              onSearchChange={find.setSearchQuery}
              onSearchKeyDown={find.handleSearchKeyDown}
              searchTrimmed={find.searchTrimmed}
              matchIndex={find.matchIndex}
              matchCount={find.matchCount}
              onPrevMatch={find.goPrevMatch}
              onNextMatch={find.goNextMatch}
            />
          </div>
        )}
      </div>

      <div
        ref={scrollContainerRef}
        className={cn(
          'min-h-0 flex-1 overflow-y-auto',
          !isMobile && 'show-scrollbar'
        )}
      >
        {treeIsEmpty ? (
          <div
            className={cn(
              'flex min-h-[min(320px,50vh)] flex-col items-center justify-center gap-2 rounded-lg',
              'text-center'
            )}
          >
            <p className='m-0 text-base font-medium text-primary-darkened'>
              You have no agency assigned
            </p>
            <p className='m-0 text-sm text-grey'>
              Contact your upline if this is unexpected
            </p>
          </div>
        ) : (
          <AgencyHierarchyList data={roots} />
        )}
      </div>
    </div>
  );
}

AgencyHierarchyView.propTypes = {
  agenciesTree: propTypes.array,
  getAgenciesTree: propTypes.func,
  setLocalLoader: propTypes.func
};
